import React, { useEffect, useState } from "react";
import { Modal, TextInput, Button, Group, Stack } from "@mantine/core";
import { IconPencil } from "@tabler/icons-react";
import { Student } from "../types/type.student";

interface StudentModalProps {
  opened: boolean;
  onClose: () => void;
  onSubmit: (student: Student) => Promise<boolean | void>;
  student: Student | null;
  mode: "create" | "edit" | "view";
  loading: boolean;
  // excludeStudentId lets an edited student keep their own email
  checkEmailExists: (email: string, excludeStudentId?: string) => boolean;
}

type FormErrors = Partial<Record<"name" | "email" | "mobile" | "enrollDate", string>>;

const todayISO = () => new Date().toISOString().split("T")[0];

const emptyForm = (): Student => ({
  student_id: "",
  name: "",
  email: "",
  mobile: "",
  enrollDate: todayISO(),
});

// <input type="date"> needs yyyy-mm-dd
const toDateInput = (value?: string) => (value ? value.slice(0, 10) : "");

export const StudentModal: React.FC<StudentModalProps> = ({
  opened,
  onClose,
  onSubmit,
  student,
  mode,
  loading,
  checkEmailExists,
}) => {
  const [form, setForm] = useState<Student>(emptyForm());
  const [errors, setErrors] = useState<FormErrors>({});
  const [editing, setEditing] = useState(false);

  // Reset every time the modal opens or a different student is shown
  useEffect(() => {
    if (!opened) return;
    setForm(
      student
        ? { ...student, enrollDate: toDateInput(student.enrollDate) }
        : emptyForm()
    );
    setErrors({});
    setEditing(mode !== "view");
  }, [opened, student, mode]);

  const isCreate = mode === "create";
  const readOnly = !editing;

  const title = isCreate ? "Create student" : editing ? "Edit student" : "Student details";

  const setField = (field: keyof Student, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const validate = (): boolean => {
    const next: FormErrors = {};

    if (!form.name?.trim()) next.name = "Enter the student's name";

    const email = form.email?.trim() || "";
    if (!email) {
      next.email = "Enter an email address";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      next.email = "Enter a valid email address";
    } else if (checkEmailExists(email, isCreate ? undefined : form.student_id)) {
      next.email = "Another student already uses this email";
    }

    if (!form.enrollDate) next.enrollDate = "Choose the enrollment date";

    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    const ok = await onSubmit({
      ...form,
      name: form.name.trim(),
      email: form.email.trim().toLowerCase(),
      mobile: form.mobile?.trim() || "",
    });
    if (ok !== false) onClose();
  };

  const handleCancel = () => {
    if (isCreate) {
      onClose();
      return;
    }
    // Discard edits and go back to the read-only view
    setForm(student ? { ...student, enrollDate: toDateInput(student.enrollDate) } : emptyForm());
    setErrors({});
    setEditing(false);
  };

  return (
    <Modal opened={opened} onClose={onClose} title={title} size="md" centered>
      <Stack gap="md">
        {/* Edit button sits at the top while viewing */}
        {!isCreate && !editing && (
          <Group justify="flex-end">
            <Button
              variant="light"
              leftSection={<IconPencil size={16} />}
              onClick={() => setEditing(true)}
            >
              Edit
            </Button>
          </Group>
        )}

        <TextInput
          label="Student ID"
          value={isCreate ? "" : form.student_id}
          placeholder={isCreate ? "Assigned automatically when you save" : undefined}
          disabled
        />

        <TextInput
          label="Name"
          value={form.name}
          onChange={(e) => setField("name", e.currentTarget.value)}
          error={errors.name}
          readOnly={readOnly}
          withAsterisk={!readOnly}
        />

        <TextInput
          label="Email"
          type="email"
          value={form.email}
          onChange={(e) => setField("email", e.currentTarget.value)}
          error={errors.email}
          readOnly={readOnly}
          withAsterisk={!readOnly}
        />

        <TextInput
          label="Mobile"
          value={form.mobile}
          onChange={(e) => setField("mobile", e.currentTarget.value)}
          error={errors.mobile}
          readOnly={readOnly}
        />

        <TextInput
          label="Enrollment date"
          type="date"
          value={form.enrollDate}
          onChange={(e) => setField("enrollDate", e.currentTarget.value)}
          error={errors.enrollDate}
          readOnly={readOnly}
          withAsterisk={!readOnly}
        />

        <Group justify="flex-end" mt="sm">
          {editing ? (
            <>
              <Button variant="default" onClick={handleCancel} disabled={loading}>
                Cancel
              </Button>
              <Button onClick={handleSave} loading={loading}>
                {isCreate ? "Create student" : "Save changes"}
              </Button>
            </>
          ) : (
            <Button variant="default" onClick={onClose}>
              Close
            </Button>
          )}
        </Group>
      </Stack>
    </Modal>
  );
};